"use client";

import type React from "react";

import { useState, useEffect } from "react";
import "./styles.css";
import API from "../../api/api";
import { useNavigate } from 'react-router-dom';

const tabValues = [
  "personal-info",
  "family-background",
  "sibling-extended-family",
  "pre-genocide",
  "separation-details",
  "post-genocide",
  "physical-clues",
  "cultural-knowledge",
  "supporting-evidence",
  "dna-preferences"
];

const QuestionnaireForm = () => {
  // Form data state to store all responses
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [progress, setProgress] = useState(10);
  const [activeTab, setActiveTab] = useState("personal-info");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const tabIcons = {
    "personal-info": <UserIcon />,
    "family-background": <UsersIcon />,
    "sibling-extended-family": <UserIcon />,
    "pre-genocide": <SchoolIcon />,
    "separation-details": <FileTextIcon />,
    "post-genocide": <FileIcon />,
    "physical-clues": <DnaIcon />,
    "cultural-knowledge": <HomeIcon />,
    "supporting-evidence": <ClipboardIcon />,
    "dna-preferences": <DnaIcon />
  };

  const tabLabels = {
    "personal-info": "Personal Info",
    "family-background": "Family Background",
    "sibling-extended-family": "Sibling & Extended Family",
    "pre-genocide": "Pre-Genocide",
    "separation-details": "Separation Details",
    "post-genocide": "Post-Genocide",
    "physical-clues": "Physical Clues",
    "cultural-knowledge": "Cultural Knowledge",
    "supporting-evidence": "Supporting Evidence",
    "dna-preferences": "DNA Preferences"
  };

  // Load saved form data from localStorage on component mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem("questionnaireFormData");
      const savedTab = localStorage.getItem("questionnaireActiveTab");

      if (savedData) {
        setFormData(JSON.parse(savedData));
      }

      if (savedTab && tabValues.includes(savedTab)) {
        setActiveTab(savedTab);
        const currentIndex = tabValues.indexOf(savedTab);
        setProgress(((currentIndex + 1) / tabValues.length) * 100);
      }
    } catch (error) {
      console.error("Error loading saved form data:", error);
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      try {
        localStorage.setItem("questionnaireFormData", JSON.stringify(formData));
        localStorage.setItem("questionnaireActiveTab", activeTab);
      } catch (error) {
        console.error("Error saving form data:", error);
      }
    }
  }, [formData, activeTab]);

  // Handle input changes and update formData
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Show saving indicator
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
    }, 500);
  };

  // Handle radio button changes
  const handleRadioChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Show saving indicator
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
    }, 500);
  };

  interface TabChangeHandlerProps {
    value: string;
  }

  const handleTabChange = (value: TabChangeHandlerProps["value"]): void => {
    setActiveTab(value);
    const currentIndex = tabValues.indexOf(value);
    setProgress(((currentIndex + 1) / tabValues.length) * 100);
  };

  // Submit form data to backend
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await API.post('/pool', formData);
      
      if (response.status === 201) {
        localStorage.removeItem("questionnaireFormData");
        localStorage.removeItem("questionnaireActiveTab");
        setProgress(10);
        setFormData({});
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to submit form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "personal-info":
        return renderPersonalInfoTab();
      case "family-background":
        return renderFamilyBackgroundTab();
      case "sibling-extended-family":
        return renderSiblingExtendedFamilyTab();
      case "pre-genocide":
        return renderPreGenocideTab();
      case "separation-details":
        return renderSeparationDetailsTab();
      case "post-genocide":
        return renderPostGenocideTab();
      case "physical-clues":
        return renderPhysicalCluesTab();
      case "cultural-knowledge":
        return renderCulturalKnowledgeTab();
      case "supporting-evidence":
        return renderSupportingEvidenceTab();
      case "dna-preferences":
        return renderDnaPreferencesTab();
      default:
        return renderPersonalInfoTab();
    }
  };

  const renderPersonalInfoTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <UserIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Personal Information</h3>
              <p className="info-card-description">
                Let's start with some basic information about you. This will
                help us identify potential family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="info-card blue security">
          <div className="info-card-content">
            <ShieldIcon className="small-icon" />
            <div>
              <h3 className="info-card-title small">
                Your information is safe with us.
              </h3>
              <p className="info-card-description">
                All data is encrypted and only used for family matching
                purposes. We understand the sensitivity of this information.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">What is your full name?</h3>
            <p className="question-description">
              Please enter your complete name as you remember it from childhood.
            </p>
            <input
              type="text"
              name="fullName"
              placeholder="Enter full name"
              className="form-input"
              value={formData.fullName || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you have any other names or nicknames you were called as a
              child?
            </h3>
            <input
              type="text"
              name="nicknames"
              placeholder="Enter nicknames"
              className="form-input"
              value={formData.nicknames || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              What is your date of birth or estimated year of birth?
            </h3>
            <p className="question-description">
              If you don't know the exact date, an estimate is helpful.
            </p>
            <input
              type="date"
              name="dateOfBirth"
              placeholder="mm/dd/yyyy"
              className="form-input"
              value={formData.dateOfBirth || ""}
              onChange={handleInputChange}
            />
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="exact"
                  name="dateAccuracy"
                  value="exact"
                  checked={formData.dateAccuracy === "exact"}
                  onChange={() => handleRadioChange("dateAccuracy", "exact")}
                />
                <label htmlFor="exact">I know my exact date</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="estimate"
                  name="dateAccuracy"
                  value="estimate"
                  checked={formData.dateAccuracy === "estimate"}
                  onChange={() => handleRadioChange("dateAccuracy", "estimate")}
                />
                <label htmlFor="estimate">This is an estimate</label>
              </div>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">Where were you born?</h3>
            <p className="question-description">
              Please be as specific as possible - village, district, and
              country.
            </p>
            <div className="input-grid">
              <input
                type="text"
                name="birthCity"
                placeholder="City/Village"
                className="form-input"
                value={formData.birthCity || ""}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="birthDistrict"
                placeholder="District"
                className="form-input"
                value={formData.birthDistrict || ""}
                onChange={handleInputChange}
              />
            </div>
            <input
              type="text"
              name="birthCountry"
              placeholder="Country"
              className="form-input"
              value={formData.birthCountry || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Have you ever lived in another country or refugee camp?
            </h3>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="yes-refugee"
                  name="refugeeStatus"
                  value="yes"
                  checked={formData.refugeeStatus === "yes"}
                  onChange={() => handleRadioChange("refugeeStatus", "yes")}
                />
                <label htmlFor="yes-refugee">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="no-refugee"
                  name="refugeeStatus"
                  value="no"
                  checked={formData.refugeeStatus === "no"}
                  onChange={() => handleRadioChange("refugeeStatus", "no")}
                />
                <label htmlFor="no-refugee">No</label>
              </div>
            </div>
            <div>
              <label htmlFor="refugee-details" className="textarea-label">
                If yes, where and when?
              </label>
              <textarea
                id="refugee-details"
                name="refugeeDetails"
                placeholder="Please provide details of locations and dates if known"
                className="form-textarea"
                value={formData.refugeeDetails || ""}
                onChange={handleInputChange}></textarea>
            </div>
          </div>
        </div>

        <div className="navigation-buttons">
          <div></div>
          <button
            onClick={() => handleTabChange("family-background")}
            className="next-button">
            Continue to Family Background
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderFamilyBackgroundTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <UsersIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Family Background</h3>
              <p className="info-card-description">
                Information about your parents and grandparents can help
                establish family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              What are the names of your known parents (father & mother)?
            </h3>
            <input
              type="text"
              name="fatherName"
              placeholder="Father's name"
              className="form-input"
              value={formData.fatherName || ""}
              onChange={handleInputChange}
            />
            <input
              type="text"
              name="motherName"
              placeholder="Mother's name"
              className="form-input"
              value={formData.motherName || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you know if your parents had any other names or nicknames?
            </h3>
            <input
              type="text"
              name="parentsNicknames"
              placeholder="Parents' nicknames"
              className="form-input"
              value={formData.parentsNicknames || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">Where were your parents born?</h3>
            <div className="input-grid">
              <input
                type="text"
                name="fatherBirthplace"
                placeholder="Father's birthplace"
                className="form-input"
                value={formData.fatherBirthplace || ""}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="motherBirthplace"
                placeholder="Mother's birthplace"
                className="form-input"
                value={formData.motherBirthplace || ""}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you know the names of your grandparents? (Maternal & Paternal)
            </h3>
            <input
              type="text"
              name="maternalGrandparents"
              placeholder="Maternal grandparents' names"
              className="form-input"
              value={formData.maternalGrandparents || ""}
              onChange={handleInputChange}
            />
            <input
              type="text"
              name="paternalGrandparents"
              placeholder="Paternal grandparents' names"
              className="form-input"
              value={formData.paternalGrandparents || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Did your parents have siblings? If yes, what were their names?
            </h3>
            <textarea
              name="parentsSiblings"
              placeholder="Parents' siblings names"
              className="form-textarea"
              value={formData.parentsSiblings || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("personal-info")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Personal Info
          </button>
          <button
            onClick={() => handleTabChange("sibling-extended-family")}
            className="next-button">
            Continue to Sibling & Extended Family
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderSiblingExtendedFamilyTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <UserIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">
                Sibling & Extended Family Information
              </h3>
              <p className="info-card-description">
                Information about your siblings and extended family can help
                establish family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you have any known brothers or sisters?
            </h3>
            <textarea
              name="siblings"
              placeholder="Siblings' names and details"
              className="form-textarea"
              value={formData.siblings || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              What was their approximate age in 1994?
            </h3>
            <textarea
              name="siblingsAges"
              placeholder="Siblings' ages in 1994"
              className="form-textarea"
              value={formData.siblingsAges || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Did your family belong to any extended family clan or group?
            </h3>
            <input
              type="text"
              name="familyClan"
              placeholder="Extended family clan or group"
              className="form-input"
              value={formData.familyClan || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you remember any cousins, aunts, or uncles?
            </h3>
            <textarea
              name="extendedFamily"
              placeholder="Cousins, aunts, or uncles details"
              className="form-textarea"
              value={formData.extendedFamily || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("family-background")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Family Background
          </button>
          <button
            onClick={() => handleTabChange("pre-genocide")}
            className="next-button">
            Continue to Pre-Genocide
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderPreGenocideTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <SchoolIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Pre-Genocide Memories</h3>
              <p className="info-card-description">
                Information about your life before the Genocide can help
                establish family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Where was your family living before the Genocide?
            </h3>
            <input
              type="text"
              name="preGenocideResidence"
              placeholder="Family's residence before Genocide"
              className="form-input"
              value={formData.preGenocideResidence || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              What did your parents do for work?
            </h3>
            <input
              type="text"
              name="parentsOccupations"
              placeholder="Parents' occupations"
              className="form-input"
              value={formData.parentsOccupations || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Did your family own any land, property, or livestock?
            </h3>
            <textarea
              name="familyProperty"
              placeholder="Details of land, property, or livestock"
              className="form-textarea"
              value={formData.familyProperty || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Did you go to school before 1994? If yes, where?
            </h3>
            <input
              type="text"
              name="preGenocideSchool"
              placeholder="School name and location"
              className="form-input"
              value={formData.preGenocideSchool || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you recall the name of a childhood friend or neighbor?
            </h3>
            <input
              type="text"
              name="childhoodFriend"
              placeholder="Friend or neighbor's name"
              className="form-input"
              value={formData.childhoodFriend || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("sibling-extended-family")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Sibling & Extended Family
          </button>
          <button
            onClick={() => handleTabChange("separation-details")}
            className="next-button">
            Continue to Separation Details
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderSeparationDetailsTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <FileTextIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Separation Details</h3>
              <p className="info-card-description">
                Information about your separation from your family can help
                establish connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              How did you get separated from your family?
            </h3>
            <textarea
              name="separationDetails"
              placeholder="Details of separation"
              className="form-textarea"
              value={formData.separationDetails || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Where were you during the Genocide (district, village, or camp)?
            </h3>
            <input
              type="text"
              name="locationDuringGenocide"
              placeholder="Location during Genocide"
              className="form-input"
              value={formData.locationDuringGenocide || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Were you with any known family members when you got separated?
            </h3>
            <textarea
              name="familyDuringSeparation"
              placeholder="Family members present during separation"
              className="form-textarea"
              value={formData.familyDuringSeparation || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Were you taken to an orphanage or refugee camp? If yes, which one?
            </h3>
            <input
              type="text"
              name="orphanageOrCamp"
              placeholder="Orphanage or refugee camp name"
              className="form-input"
              value={formData.orphanageOrCamp || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you remember who helped or took care of you after the war?
            </h3>
            <textarea
              name="postWarCaretakers"
              placeholder="Details of caretakers after the war"
              className="form-textarea"
              value={formData.postWarCaretakers || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("pre-genocide")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Pre-Genocide
          </button>
          <button
            onClick={() => handleTabChange("post-genocide")}
            className="next-button">
            Continue to Post-Genocide
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderPostGenocideTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <FileIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Post-Genocide Life & Identity</h3>
              <p className="info-card-description">
                Information about your life after the Genocide can help
                establish family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Did you grow up in an orphanage, foster home, or with a guardian?
            </h3>
            <textarea
              name="postGenocideUpbringing"
              placeholder="Details of upbringing after Genocide"
              className="form-textarea"
              value={formData.postGenocideUpbringing || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Have you taken any steps before to trace your family?
            </h3>
            <textarea
              name="previousTracingEfforts"
              placeholder="Previous efforts to trace family"
              className="form-textarea"
              value={formData.previousTracingEfforts || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Have you undergone any DNA testing before?
            </h3>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="yes-dna"
                  name="previousDnaTesting"
                  value="yes"
                  checked={formData.previousDnaTesting === "yes"}
                  onChange={() =>
                    handleRadioChange("previousDnaTesting", "yes")
                  }
                />
                <label htmlFor="yes-dna">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="no-dna"
                  name="previousDnaTesting"
                  value="no"
                  checked={formData.previousDnaTesting === "no"}
                  onChange={() => handleRadioChange("previousDnaTesting", "no")}
                />
                <label htmlFor="no-dna">No</label>
              </div>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you have any official documents (birth certificates, old
              pictures, school records)?
            </h3>
            <textarea
              name="officialDocuments"
              placeholder="Details of official documents"
              className="form-textarea"
              value={formData.officialDocuments || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("separation-details")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Separation Details
          </button>
          <button
            onClick={() => handleTabChange("physical-clues")}
            className="next-button">
            Continue to Physical Clues
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderPhysicalCluesTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <DnaIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Physical & Biological Clues</h3>
              <p className="info-card-description">
                Information about your physical traits can help establish family
                connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you have any birthmarks, scars, or unique physical traits that
              your family might recognize?
            </h3>
            <textarea
              name="physicalTraits"
              placeholder="Details of birthmarks, scars, or unique physical traits"
              className="form-textarea"
              value={formData.physicalTraits || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Were you told any unique genetic traits run in your family (e.g.,
              eye color, height, skin tone)?
            </h3>
            <textarea
              name="geneticTraits"
              placeholder="Details of genetic traits"
              className="form-textarea"
              value={formData.geneticTraits || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Does your family have any known hereditary health conditions?
            </h3>
            <textarea
              name="hereditaryConditions"
              placeholder="Details of hereditary health conditions"
              className="form-textarea"
              value={formData.hereditaryConditions || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("post-genocide")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Post-Genocide
          </button>
          <button
            onClick={() => handleTabChange("cultural-knowledge")}
            className="next-button">
            Continue to Cultural Knowledge
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderCulturalKnowledgeTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <HomeIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">
                Cultural & Ancestral Knowledge
              </h3>
              <p className="info-card-description">
                Information about your cultural background can help establish
                family connections.
              </p>
            </div>
          </div>
        </div>

        {/* <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you know which Rwandan clan or lineage your family belonged to?
            </h3>
            <input
              type="text"
              name="rwandanClan"
              placeholder="Rwandan clan or lineage"
              className="form-input"
              value={formData.rwandanClan || ""}
              onChange={handleInputChange}
            />
          </div>
        </div> */}

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you remember any cultural traditions or family stories passed
              down?
            </h3>
            <textarea
              name="culturalTraditions"
              placeholder="Cultural traditions or family stories"
              className="form-textarea"
              value={formData.culturalTraditions || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Did your family practice any specific religious or traditional
              customs?
            </h3>
            <textarea
              name="religiousCustoms"
              placeholder="Religious or traditional customs"
              className="form-textarea"
              value={formData.religiousCustoms || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("physical-clues")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Physical Clues
          </button>
          <button
            onClick={() => handleTabChange("supporting-evidence")}
            className="next-button">
            Continue to Supporting Evidence
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderSupportingEvidenceTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <ClipboardIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">Supporting Evidence & Leads</h3>
              <p className="info-card-description">
                Any supporting evidence can help establish family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you have any old photos, letters, or family artifacts?
            </h3>
            <textarea
              name="familyArtifacts"
              placeholder="Details of old photos, letters, or family artifacts"
              className="form-textarea"
              value={formData.familyArtifacts || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you have contacts of any people who may remember your family?
            </h3>
            <textarea
              name="familyContacts"
              placeholder="Contacts of people who may remember your family"
              className="form-textarea"
              value={formData.familyContacts || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Have you connected with any family tracing organizations before?
            </h3>
            <textarea
              name="tracingOrganizations"
              placeholder="Details of previous connections with family tracing organizations"
              className="form-textarea"
              value={formData.tracingOrganizations || ""}
              onChange={handleInputChange}></textarea>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("cultural-knowledge")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Cultural Knowledge
          </button>
          <button
            onClick={() => handleTabChange("dna-preferences")}
            className="next-button">
            Continue to DNA Preferences
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    );
  };

  const renderDnaPreferencesTab = () => {
    return (
      <div className="tab-content">
        <div className="info-card blue">
          <div className="info-card-content">
            <DnaIcon className="large-icon" />
            <div>
              <h3 className="info-card-title">
                DNA & Technology Matching Preferences
              </h3>
              <p className="info-card-description">
                Your preferences regarding DNA and technology matching can help
                establish family connections.
              </p>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Are you open to taking a DNA test for familial matching?
            </h3>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="yes-dna-test"
                  name="dnaTestConsent"
                  value="yes"
                  checked={formData.dnaTestConsent === "yes"}
                  onChange={() => handleRadioChange("dnaTestConsent", "yes")}
                />
                <label htmlFor="yes-dna-test">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="no-dna-test"
                  name="dnaTestConsent"
                  value="no"
                  checked={formData.dnaTestConsent === "no"}
                  onChange={() => handleRadioChange("dnaTestConsent", "no")}
                />
                <label htmlFor="no-dna-test">No</label>
              </div>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Would you be comfortable with AI using your data to find potential
              family connections?
            </h3>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="yes-ai"
                  name="aiConsent"
                  value="yes"
                  checked={formData.aiConsent === "yes"}
                  onChange={() => handleRadioChange("aiConsent", "yes")}
                />
                <label htmlFor="yes-ai">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="no-ai"
                  name="aiConsent"
                  value="no"
                  checked={formData.aiConsent === "no"}
                  onChange={() => handleRadioChange("aiConsent", "no")}
                />
                <label htmlFor="no-ai">No</label>
              </div>
            </div>
          </div>
        </div>

        <div className="question-card">
          <div className="question-card-content">
            <h3 className="question-title">
              Do you consent to your anonymized data being used to improve AI
              matching?
            </h3>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="yes-consent"
                  name="anonymizedDataConsent"
                  value="yes"
                  checked={formData.anonymizedDataConsent === "yes"}
                  onChange={() =>
                    handleRadioChange("anonymizedDataConsent", "yes")
                  }
                />
                <label htmlFor="yes-consent">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="no-consent"
                  name="anonymizedDataConsent"
                  value="no"
                  checked={formData.anonymizedDataConsent === "no"}
                  onChange={() =>
                    handleRadioChange("anonymizedDataConsent", "no")
                  }
                />
                <label htmlFor="no-consent">No</label>
              </div>
            </div>
          </div>
        </div>

        <div className="navigation-buttons">
          <button
            onClick={() => handleTabChange("supporting-evidence")}
            className="back-button">
            <ChevronLeftIcon />
            Back to Supporting Evidence
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className="next-button"
          >
            {isLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="questionnaire-form">
      <div className="progress-header">
        <div className="step-indicator">
          Step {tabValues.indexOf(activeTab) + 1} of {tabValues.length}
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
        </div>
        {isSaving && <div className="saving-indicator">Saving...</div>}
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <div className="tabs-list">
            {tabValues.map((tab) => (
              <button
                key={tab}
                className={`tab-button ${activeTab === tab ? "active" : ""}`}
                onClick={() => handleTabChange(tab)}>
                {tabIcons[tab as keyof typeof tabIcons]}
                <span>{tabLabels[tab as keyof typeof tabLabels]}</span>
              </button>
            ))}
          </div>
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
};

// Icon components
const UserIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UsersIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SchoolIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
  </svg>
);

const FileTextIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const FileIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const DnaIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M2 15c6.667-6 13.333 0 20-6" />
    <path d="M9 22c1.798-1.998 2.518-3.995 2.807-5.993" />
    <path d="M15 2c-1.798 1.998-2.518 3.995-2.807 5.993" />
    <path d="M17 6c-2.5 1.5-5 3-7 3s-4.5-1.5-7-3" />
    <path d="M17 18c-2.5-1.5-5-3-7-3s-4.5 1.5-7 3" />
  </svg>
);

const HomeIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ClipboardIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const ShieldIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    className="chevron-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    className="chevron-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export default QuestionnaireForm;
